/* Read-only price checks. No credentials, cart writes or checkout requests. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CYPBespokeVerification = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const catalogue = globalThis.CYPCatalogue || {};
  const integer = value => Number.isSafeInteger(Number(value)) && /^\d+$/.test(String(value)) && Number(value) > 0;
  function requestLines(lines) {
    if (!Array.isArray(lines) || !lines.length || lines.length > 20) throw Error('Provide between 1 and 20 bespoke items.');
    return lines.map(line => {
      const productId = String(line.productId);
      if (!Object.hasOwn(catalogue, productId)) throw Error('This product is not available for online sizing.');
      if (![line.widthMM, line.heightMM, line.quantity].every(integer) || Number(line.quantity) > 99) throw Error('Enter whole millimetres and a quantity from 1 to 99.');
      return { productId, widthMM: Number(line.widthMM), heightMM: Number(line.heightMM), quantity: Number(line.quantity) };
    });
  }
  const key = lines => JSON.stringify(requestLines(lines));
  function validate(data, lines, now = Date.now()) {
    const expected = requestLines(lines);
    if (!data || data.currency !== 'GBP' || data.checkoutEnabled !== false || data.pricingIsDummy !== false ||
        data.pricesIncludeTax !== true || data.deliveryIncluded !== true || typeof data.pricingVersion !== 'string' || data.stockSizeMatching !== 'standard-variant' ||
        !Number.isSafeInteger(data.expiresAt) || data.expiresAt <= now || data.expiresAt > now + 16 * 60000 ||
        !Array.isArray(data.lines) || data.lines.length !== expected.length) throw Error('The service returned an invalid price check. Please retry.');
    const pricing = data.pricing;
    const rules = ['squareMetreNetPence', 'minimumAreaMM2', 'deliveryThresholdMM', 'smallDeliveryNetPence', 'largeDeliveryNetPence', 'vatBasisPoints'];
    if (!pricing || rules.some(name => !Number.isSafeInteger(pricing[name]) || pricing[name] < 0)) throw Error('The price is unavailable. Please try again.');
    let total = 0;
    const clean = data.lines.map((line, index) => {
      const wanted = expected[index], configured = catalogue[wanted.productId], variantId = configured.variantId;
      if (!line || Object.keys(wanted).some(name => line[name] !== wanted[name]) || !['Standard', 'Bespoke'].includes(line.sizingType) ||
          !/^[1-9]\d*$/.test(line.variantId) || typeof line.sku !== 'string' || !line.sku.trim() || typeof line.title !== 'string' ||
          !Number.isSafeInteger(line.unitPence) || line.unitPence < 0 || !Number.isSafeInteger(line.unitPence * line.quantity)) throw Error('The service returned a price for different measurements. Please retry.');
      const stock = line.sizingType === 'Standard';
      const deliveryTitle = stock ? 'Free Standard Delivery — 48 hours' : 'Free Standard Bespoke Delivery — 10–14 days';
      if (line.pricingSource !== (stock ? 'stock-variant' : 'bespoke-formula') || line.deliveryTitle !== deliveryTitle ||
          (stock ? line.variantId === variantId : line.variantId !== variantId || line.sku !== configured.sku)) throw Error('The stock size or delivery could not be verified. Please retry.');
      total += line.unitPence * line.quantity;
      return Object.freeze({ ...wanted, variantId: line.variantId, sku: line.sku, title: line.title, image: line.image || '', handle: line.handle || '', unitPence: line.unitPence, sizingType: line.sizingType, pricingSource: line.pricingSource, deliveryTitle });
    });
    if (!Number.isSafeInteger(total) || total !== data.subtotalPence) throw Error('The service returned an invalid total. Please retry.');
    return Object.freeze({ lines: Object.freeze(clean), subtotalPence: total, currency: 'GBP', checkoutEnabled: false,
      customerCheckoutEnabled: data.customerCheckoutEnabled === true,
      expiresAt: data.expiresAt, pricingVersion: data.pricingVersion, pricingIsDummy: data.pricingIsDummy,
      pricesIncludeTax: true, deliveryIncluded: true, pricing: Object.freeze(Object.fromEntries(rules.map(name => [name, pricing[name]]))), key: key(lines) });
  }
  function createClient(fetcher, endpoint, now = () => Date.now()) {
    // Relative storefront route only: the app secret never belongs in the theme.
    if (!/^\/(?!\/)[^?#]+\/catalogue-price$/.test(endpoint)) throw Error('The price-check connection is not configured.');
    return {
      async verify(lines, signal) {
        const body = JSON.stringify({ lines: requestLines(lines) });
        const controller = new AbortController();
        const cancel = () => controller.abort();
        if (signal?.aborted) cancel();
        signal?.addEventListener('abort', cancel, { once: true });
        const timer = setTimeout(cancel, 15000);
        try {
          const response = await fetcher(endpoint, { method: 'POST', credentials: 'same-origin', cache: 'no-store',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body, signal: controller.signal });
          if (!response.ok) {
            const failure = await response.json().catch(() => null);
            if (failure?.code === 'STOCK_UNAVAILABLE') throw Error('This matches a stock size, but the quantity is unavailable for standard delivery.');
            if (response.status === 429) throw Error('Too many price checks. Wait a minute, then retry.');
            throw Error('We could not check this price. Please retry.');
          }
          if (!response.headers.get('content-type')?.includes('application/json')) throw Error('The price-check connection is not ready yet.');
          return validate(await response.json(), lines, now());
        } catch (error) {
          if (controller.signal.aborted) throw Error('The price check was interrupted. Please retry.');
          if (error instanceof TypeError) throw Error('Prices are temporarily unavailable. Please retry.');
          throw error;
        } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
      }
    };
  }
  function fresh(quote, lines, now = Date.now()) {
    try { return Boolean(quote && quote.expiresAt > now && quote.key === key(lines)); } catch (_) { return false; }
  }
  return Object.freeze({ key, requestLines, validate, createClient, fresh });
});
